// The word cloud's config, owned here rather than by the shell.
//
// It cannot simply be resolved on every read: the config is edited between param writes, and the
// param store lags a write (the page-model attribute moves first, the store catches up when the
// local changes resolve). A fresh resolve in that window would answer with the config as it was
// before the edit and drop it. So one copy lives here -- seeded from the stored param, rebuilt
// whenever the corpus moves under it, and composed into its param on every change for the shell
// to write down. The shell stays the only writer.
//
// Two of the preset-derived fields, the word cap and the font floor, are scaled to the corpus, so
// a block that opens on any view mode other than the cloud resolves them against no words at all
// and has to resolve again once the words arrive.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getStoredReportParams, getWcCustomPresets, setWcCustomPresets, triggerPresetsUpdate } from '@aytm/app/stats/lib/stat-page'
import { WC_LIB } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/sync'
import { saveWcPreset } from '@aytm/app/stats/page/question-views/components/word-cloud/lib/api'
import {
  calcWcParamsSection,
  calcWcParamsString,
  exportWcConfig,
  importWcConfig,
  resolveWcConfig,
  wcParamName
} from '@aytm/app/stats/page/question-views/components/word-cloud/lib/report-params'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// The bag an edit stands for when it names a preset: a named one by its id, one the report saved
// by its place in the list. Nothing for an edit naming no preset -- the custom one included, since
// that is the fields themselves rather than a preset to reproduce.
const presetBag = id => {
  if ('number' === typeof id) return getWcCustomPresets()?.[id - 1] || null

  return WC_LIB.presetConfig(id) ? { preset: id } : null
}

export default function useWcConfig({
  id,
  questionKey,
  modelDefaults,
  rejects,
  corpusSize = 0,
  storedSection,
  paramsRevision,
  onOutdatedPreset,
  onParamsChange
} = {}) {
  // everything the resolve reads, refreshed each render so it never has to re-bind
  const inputs = useRef()
  inputs.current = { id, questionKey, modelDefaults, rejects, corpusSize, storedSection, onOutdatedPreset }

  // the section is handed over rather than re-read: the store lags the write that prompted this
  const resolve = useCallback((size, section = inputs.current.storedSection) => {
    const { id, modelDefaults, rejects, onOutdatedPreset } = inputs.current

    return resolveWcConfig({
      id,
      section,
      lib: WC_LIB,
      modelDefaults,
      rejects,
      corpusSize: size,
      onOutdatedPreset
    })
  }, [])

  const [config, setConfig] = useState(() => resolve(corpusSize))

  const configRef = useRef(config)
  configRef.current = config

  const onParamsChangeRef = useRef(onParamsChange)
  onParamsChangeRef.current = onParamsChange

  // Only a corpus the config has not been measured against yet is worth resolving again, and a
  // stored section that moved outside the block replaces what is held.
  //
  // Keyed on the revision the shell bumps per host render as well as on the section. The cloud's
  // own write is silent, so the section it was handed never moves with its edits -- and a report
  // reset back to a stored mask, or to no section twice over, lands on the very value props last
  // carried, leaving nothing for a comparison of the section alone to notice.
  const lastResolved = useRef({ corpusSize, storedSection, paramsRevision })
  useEffect(() => {
    const last = lastResolved.current
    if (
      last.corpusSize === corpusSize &&
      last.storedSection === storedSection &&
      last.paramsRevision === paramsRevision
    ) {
      return
    }

    lastResolved.current = { corpusSize, storedSection, paramsRevision }
    setConfig(resolve(corpusSize))
  }, [corpusSize, storedSection, paramsRevision, resolve])

  // the keyed section a bulk redraw merges across every cloud on the page
  const getWcParamsSection = useCallback(config => {
    const { id, modelDefaults, rejects } = inputs.current

    return calcWcParamsSection({ id, config: config || configRef.current, lib: WC_LIB, modelDefaults, rejects })
  }, [])

  // what the shell writes under this cloud's own param, or nothing when there is nothing to say
  const getWcParams = useCallback(config => {
    const { modelDefaults, rejects } = inputs.current

    return calcWcParamsString({ config: config || configRef.current, lib: WC_LIB, modelDefaults, rejects }) || null
  }, [])

  const getWcConfig = useCallback(() => configRef.current, [])

  // The bag another cloud is handed so it can be drawn the same way, and the way back in.
  const getWcPreset = useCallback(expType => {
    const { rejects } = inputs.current

    return exportWcConfig({ config: configRef.current, lib: WC_LIB, rejects, expType })
  }, [])

  // Resolving a bag commits nothing: a bulk redraw only wants the param each cloud would end up
  // with, and applying it there would write one config per block on the way.
  const importWcPreset = useCallback(preset => {
    const { rejects, corpusSize, onOutdatedPreset } = inputs.current

    return importWcConfig({
      preset,
      config: { ...configRef.current },
      lib: WC_LIB,
      rejects,
      corpusSize,
      onOutdatedPreset
    })
  }, [])

  // A host handing this cloud a preset to keep says whether it is to be written down too.
  const applyWcPreset = useCallback(
    (preset, update = true) => {
      const next = importWcPreset(preset)

      setConfig(next)
      if (update) onParamsChangeRef.current?.(getWcParams(next))

      return next
    },
    [importWcPreset, getWcParams]
  )

  // An edit names the fields it moves; the rest of the config stands. Naming a preset is the one
  // edit that does not work that way -- a preset owns its fields whole, so the cloud has to be
  // redrawn in it rather than relabelled, or the param it composes reproduces the look it left.
  const change = useCallback(
    params => {
      const preset = presetBag(params.currentPresetId)
      const next = preset ? importWcPreset(preset) : { ...configRef.current, ...params }

      setConfig(next)
      onParamsChangeRef.current?.(getWcParams(next))
    },
    [importWcPreset, getWcParams]
  )

  // Saving the cloud as a custom preset: the report keeps it, and every other cloud on the page
  // is told to rebuild the list it picks from. A shared report has nothing to save back to.
  const onSave = useCallback(async () => {
    if (PageGlobals.SHARE_KEY) return

    const { wc_custom_presets: presets } = await saveWcPreset({
      questionKey: inputs.current.questionKey,
      preset: getWcPreset('custom')
    })

    if (!presets) return

    setWcCustomPresets(presets)
    triggerPresetsUpdate()
  }, [getWcPreset])

  // The cloud goes back to the one the report was saved with: the edits held on the page since
  // are dropped by writing the stored param back over them.
  const onReset = useCallback(() => {
    const saved = getStoredReportParams()[wcParamName(inputs.current.id)] || null

    onParamsChangeRef.current?.(saved)
    // ...and the panel goes back with it. Writing the param alone left the controls showing a
    // configuration the report no longer holds, and with nothing stored it wrote an empty value
    // over an empty one, so nothing moved at all. Resolved from the saved param, not the section
    // the host last handed over: that one is the edit being dropped, and so is what the store
    // answers while its local changes are pending -- the explicit null keeps it from being asked.
    setConfig(resolve(inputs.current.corpusSize, saved))
  }, [resolve])

  const handlers = useMemo(
    () => ({
      onSave,
      onReset,
      onParamsChange: params => change(params),
      onMaskUploaded: data => {
        const uploaded = data && 0 < data.original.url.length

        change({
          mask: '',
          customMaskId: uploaded ? data.id.toString() : '-1',
          customMaskPath: uploaded ? data.original.url : ''
        })
      },
      onWordClick: ({ text }) => change({ activeWord: text })
    }),
    [change, onSave, onReset]
  )

  // the bag the shell reaches the cloud through; every reader is stable, so the props built off
  // it do not turn over on a repaint
  const api = useMemo(
    () => ({ getWcConfig, getWcPreset, importWcPreset, applyWcPreset, getWcParamsSection }),
    [getWcConfig, getWcPreset, importWcPreset, applyWcPreset, getWcParamsSection]
  )

  return { config, handlers, api }
}
